// Auth + login lifecycle. Migrated from the inline <script> block in
// admin/index.php during Phase 6a (June 2026).
//
// Functions here take state: TimestopState + config: ApiFetchConfig so they
// are pure functions of (state, config) and can be unit-tested without a
// browser. The inline script's const-arrow wrappers are what the HTML
// onclick handlers actually call — they read window.timestop.state +
// window.apiFetchCore's config and forward here.
//
// State is mutated via direct property assignment (state.currentUser = X),
// which is safe because the inline scripts read the same object via the
// shared `let X = window.timestop.state.X` bindings.

import type { ApiFetchConfig, ApiResponse } from './api';
import type { TimestopState } from './state';
import type { CurrentUser } from './types';
import { loadCurrencyDefaults, fetchNotify } from './data';
import { t } from './i18n';
import { ensurePushReady, logoutUnsubscribePush } from './push';

// Show the login modal and (optionally) write a message to the
// #login-error slot. Idempotent — calling twice is fine.
export function showLoginModal(message?: string): void {
  const m = document.getElementById('login-modal');
  if (!m) return;
  if (message) {
    const err = document.getElementById('login-error');
    if (err) err.textContent = message;
  }
  m.classList.remove('hidden');
}

export function hideLoginModal(): void {
  const m = document.getElementById('login-modal');
  if (m) m.classList.add('hidden');
}

// True when the logged-in user has the 'operator' role. Operators get
// a stripped-down view: no Groups / Users nav, no Frozen / Force Sync
// controls. Admins see everything.
export function isOperator(state: TimestopState): boolean {
  return !!(state.currentUser && state.currentUser.role === 'operator');
}

// Toggle `.role-admin-only` elements (4 nav items: 2 desktop + 2 mobile)
// depending on whether the current user is an operator. Inline style is
// used (not .toggle('hidden')) so the existing inline display:none on
// .nav-4 (Analytics) stays untouched.
export function applyRoleGating(state: TimestopState): void {
  const op = isOperator(state);
  document.querySelectorAll('.role-admin-only').forEach(el => {
    const node = el as HTMLElement;
    node.style.display = op ? 'none' : '';
  });
}

// Lightweight ApiFetchFn shape — we accept any function with this
// signature rather than importing the bundled apiFetchCore directly, so
// tests can swap it for a fake and the inline script can pass its own
// thin wrapper.
export type ApiFetchFn = <T = unknown>(
  action: string,
  opts: Record<string, unknown>,
  config: ApiFetchConfig,
) => Promise<ApiResponse<T>>;

// POST /api/login, then mutate state.currentUser / state.csrfToken and
// hand control to the app-shell loader. The "dashboard is empty" recovery
// reload is preserved from the inline version — when the API returned
// terminals but neither quick nor all containers have any children 50ms
// later, something silently broke during render and a full page reload is
// the cheapest reliable recovery.
export async function doLogin(
  username: string,
  password: string,
  state: TimestopState,
  config: ApiFetchConfig,
  apiFetchFn: ApiFetchFn,
  hooks: {
    loadAppShell: () => Promise<void>;
    fetchData: () => Promise<void>;
  },
): Promise<void> {
  // Phase 12.2: #login-error is now a dismissable-message container
  // (see prependLoginError below). On a fresh attempt we do NOT clear
  // it — a previous error's dismiss timer is still valid, and if the
  // user retries mid-dismiss, the old error just keeps its own
  // schedule and the new one appears on top. We only clear the slot
  // wholesale on hideLoginModal() (successful sign-in).
  console.log('[auth.doLogin]', username);
  const result = await apiFetchFn<{ ok: boolean; user?: CurrentUser; csrf?: string; error?: string }>(
    'login',
    { method: 'POST', body: { username, password } },
    config,
  );

  if (result.ok && result.data && result.data.ok && result.data.user && result.data.csrf) {
    // Wipe the dismissable error slot on a successful sign-in. The
    // modal is about to hide, so any pending dismiss timers would be
    // working on a hidden element (and would reappear if the user
    // logs out without page-reload). Wholesale clear is the right
    // thing here.
    clearLoginErrors();
    state.currentUser = result.data.user;
    state.csrfToken = result.data.csrf;
    hideLoginModal();
    // Pull the shell markup from /shell (auth-gated; lives at admin/shell.php).
    await hooks.loadAppShell();
    applyRoleGating(state);
    console.log('[auth.doLogin] fetchData on login');
    // Phase 12.16: per-ISO currency defaults (BO-wide fallback
    // table) and the data fetch are independent — run them in
    // parallel. fetchData is the one the dashboard blocks on; the
    // currency defaults can land a tick later because the
    // Dashboard's first render already uses the hard-coded
    // {div:100, fs:'%.2f '+iso} fallback. We still await it here
    // so the modal's accounting tab on first open sees the DB
    // defaults (otherwise a user clicking into a terminal in the
    // first ~100ms after login would format with the hard-coded
    // fallback and re-paint when the map arrives).
    //
    // Phase 12.17: the notify feed follows the same pattern — we
    // want the panel populated as soon as the user reaches the
    // dashboard, but a late landing won't break the KPIs. It's
    // also included in the same Promise.all so all three "first
    // paint" fetches settle at the same await point.
    await Promise.all([
      loadCurrencyDefaults(state, config),
      hooks.fetchData(),
      fetchNotify(state, config),
      // Web Push: warm the VAPID key + existing subscription
      // list. Best-effort — a server without the push table
      // migrated (yet) just sets pwa.subscribed=false, which
      // the Profile modal renders as "Subscribe".
      ensurePushReady(state, config),
    ]);
    console.log('[auth.doLogin] after fetchData terminals=' + Object.keys(state.terminals).length);

    // If the API returned terminals but the DOM is still empty 50ms later,
    // something silently broke during render — full page reload recovers.
    if (Object.keys(state.terminals).length > 0) {
      await new Promise<void>(res => setTimeout(res, 50));
      const quick = document.getElementById('quick-terminals');
      const all = document.getElementById('all-terminals');
      const quickHasKids = !!quick && quick.children.length > 0;
      const allHasKids = !!all && all.children.length > 0;
      console.log('[auth.doLogin] on dashboard', quickHasKids, allHasKids);
      if (!quickHasKids && !allHasKids) {
        console.warn('[auth.doLogin] dashboard empty after fetch — forcing reload');
        window.location.reload();
      }
    } else {
      console.warn('[auth.doLogin] dashboard empty after fetch — forcing reload');
      window.location.reload();
    }
    return;
  }

  // Login failed — write a context-appropriate message to the error slot.
  //
  // Phase 12: distinguish 429 (rate-limited) from 401 (invalid credentials).
  // The backend maps Auth::login's `reason: 'rate_limited'` onto HTTP 429
  // and `reason: 'invalid_credentials'` (and friends) onto 401. The body
  // shape is intentionally identical (`{error: 'too_many_requests'}` vs
  // `{error: 'invalid_credentials'}`) so a brute-force script that just
  // reads the body still gets the same generic message. The HTTP status
  // is the only signal the SPA uses.
  const dataAny = result.data as { error?: string } | null;
  const resultAny = result as unknown as { error?: string };
  const code = (dataAny && dataAny.error) || resultAny.error || 'unknown';
  let msg: string;
  let isRateLimited = false;
  if (result.status === 429 || code === 'too_many_requests') {
    // Rate-limited. Show a friendlier message + a 🔒 icon and disable
    // the submit button briefly so the user can't spam-click. The
    // server-side RATE_WINDOW is 15 min; we don't know how long they've
    // been locked, so we keep the button disabled for a short grace
    // period (30s) — long enough that the next attempt will probably
    // also be rejected but not so long that a legitimate typo trap
    // can't be recovered from.
    msg = t('auth.too_many_requests');
    isRateLimited = true;
    setLoginLockedTemporarily();
  } else if (code === 'invalid_credentials' || code === 'unauthorized') {
    msg = t('auth.invalid_credentials');
  } else {
    msg = t('auth.sign_in_failed', { code });
  }
  prependLoginError(msg, { isRateLimited });
}

// Lock the login form for ~30s after a 429 (rate-limit) response.
// Disables the username + password inputs (clears the password so
// the user doesn't see their secret pre-filled when they retry),
// and disables the submit button. The form fields and button get
// re-enabled by the same timeout. Visual only — the server still
// 429s on attempts that happen during the lockout. The disabled
// submit button is a no-op so the form doesn't fire API calls
// while locked.
//
// Why clear the password? Two reasons: (1) showing the typed
// password in plain text for 30s after a 429 is a small but real
// security papercut (shoulder-surfing in cafes etc.); (2) when
// the lockout ends, the user will need to type a new password
// anyway if they were rate-limited on a stale attempt — clearing
// nudges them toward a fresh retry rather than re-sending what
// they just sent.
function setLoginLockedTemporarily(): void {
  const usernameInput = document.getElementById('login-username') as HTMLInputElement | null;
  const passwordInput = document.getElementById('login-password') as HTMLInputElement | null;
  const btn = document.querySelector<HTMLButtonElement>(
    '#login-modal form button[type="submit"]',
  );
  if (usernameInput) usernameInput.disabled = true;
  if (passwordInput) {
    passwordInput.disabled = true;
    passwordInput.value = '';
  }
  if (btn) btn.disabled = true;
  setTimeout(() => {
    if (usernameInput) usernameInput.disabled = false;
    if (passwordInput) passwordInput.disabled = false;
    if (btn) btn.disabled = false;
  }, 30_000);
}

// Phase 12.2: dismissable login-error widget.
//
// #login-error is now a CONTAINER that can hold zero or more error
// chips. Each prependLoginError() call adds a fresh chip at the top
// (newest first), animates it in, and schedules it to fade out + be
// removed after a timeout. Multiple in-flight errors stack: clicking
// Sign In 3 times yields 3 chips, each dismissing on its own schedule.
//
// Timeout: 6s for the common case (one-line message the user can
// re-read by re-clicking if they need to). 15s for rate-limited
// because the message is "wait a few minutes" — the user shouldn't
// have to retype/retry to see it again, but it also shouldn't
// linger forever.
//
// Animation: simple fade-in + 4px slide-down on enter (~180ms),
// fade-out on exit (~250ms). Transitions only — no libraries, no
// motion if the user has prefers-reduced-motion.
//
// The chip is a <div role="alert"> with text-red-700 / bg-red-50 /
// border-red-200, matching the colour scheme of the rest of the
// modal. Clicking the chip dismisses it immediately (small QoL —
// the user can opt out of waiting).

interface PrependLoginErrorOpts {
  isRateLimited?: boolean;
  // Override the auto-dismiss timeout (ms). Default: 6000 (or 15000
  // for isRateLimited). Useful for tests; not exposed to callers yet.
  durationMs?: number;
}

function prependLoginError(msg: string, opts: PrependLoginErrorOpts = {}): void {
  const slot = document.getElementById('login-error');
  if (!slot) return;

  const chip = document.createElement('div');
  chip.setAttribute('role', 'alert');
  // Styling matches the slot's own text-red-600 / mb-3 colour scheme
  // but with a card-like bg/border so each chip reads as a distinct
  // unit. The leading mb-2 on every chip except the first keeps the
  // stack tidy; the slot's mb-3 handles the gap to whatever sits
  // below it (Sign in button, since #login-error is positioned
  // AFTER the button per Phase 12.2's UI change).
  const isFirst = slot.firstChild === null;
  // Phase 12.4: pointer-events: auto on the chip — the slot has
  // pointer-events: none (to avoid blocking clicks when empty), so
  // the chip must opt back in to receive its click-to-dismiss
  // gesture. max-width: min(90vw, 28rem) keeps a long message from
  // running off the viewport; with the slot anchored to bottom: 5px
  // via the toast positioning, a wide chip would be clipped by the
  // viewport edges.
  chip.className = 'login-error-chip' +
    (isFirst ? '' : ' mt-2') +
    ' rounded-2xl bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm cursor-pointer';
  chip.style.maxWidth = 'min(90vw, 28rem)';
  chip.style.pointerEvents = 'auto';
  chip.textContent = msg;
  // Initial state: invisible + offset down. rAF toggles to the
  // resting state so the CSS transition fires.
  chip.style.opacity = '0';
  chip.style.transform = 'translateY(-4px)';
  chip.style.overflow = 'hidden';
  chip.style.height = '0px';
  chip.style.transition = 'all 180ms ease-in-out';

  // Click anywhere on the chip to dismiss immediately. The chip is
  // a real button-equivalent (cursor-pointer + role=alert) so this
  // matches user expectation for dismissable inline alerts.
  const dismiss = (): void => {
    if (!chip.isConnected) return; // already removed by the timer
    chip.style.transition = 'all 420ms ease-in-out';
    chip.style.opacity = '0';
    chip.style.transform = 'translateY(-4px)';
    chip.style.height = '0px';
    setTimeout(() => { if (chip.parentNode) chip.parentNode.removeChild(chip); }, 260);
  };
  chip.addEventListener('click', dismiss);

  // Honour the user's OS-level motion preference: skip the slide
  // and just fade. (We still keep a tiny opacity transition so the
  // chip doesn't pop in jarringly.)
  const reduced = typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) chip.style.transform = 'none';

  slot.prepend(chip);
  // Force a layout so the transition runs from the initial state.
  // (Reading offsetWidth / getBoundingClientRect flushes pending
  // style changes, which is what we want here.)
  void chip.offsetWidth;
  requestAnimationFrame(() => {
    chip.style.opacity = '1';
    chip.style.transform = 'none';
    chip.style.height = 'auto';
  });

  const duration = 5_000
  //opts.durationMs ??
  //  (opts.isRateLimited ? 15_000 : 6_000);
  setTimeout(dismiss, duration);
}

// Wipe the #login-error slot wholesale. Called on a successful
// sign-in (the modal is about to hide anyway, so any pending
// dismiss timers would be working on a hidden element) and on
// doLogout (defensive — fresh page reload wipes it too).
export function clearLoginErrors(): void {
  const slot = document.getElementById('login-error');
  if (slot) slot.replaceChildren();
}

// Phase 12.4: ensure the #login-error slot is positioned as a
// toast at the bottom-center of the viewport, OUTSIDE the form
// but INSIDE #login-modal. The canonical markup in admin/index.php
// is being updated to match (slot is now a direct child of
// #login-modal, not a child of the form, and styled with
// position: fixed; bottom: 5px; left: 50%; transform:
// translateX(-50%); z-index: 60), but admin/index.php doesn't
// auto-deploy on this PC, so we also do both moves in JS here,
// idempotently, so the new behaviour ships via the Vite tunnel.
// When the canonical markup eventually catches up, both branches
// are no-ops.
//
// Why outside the form? Errors are now decoupled from form
// submission flow: they render as a floating toast at the bottom
// of the viewport instead of inlining inside the modal's form
// area. Visually less noisy; multiple errors stack without
// pushing the form around.
export function ensureLoginErrorPlacement(): void {
  const slot = document.getElementById('login-error');
  if (!slot) return;
  const modal = document.getElementById('login-modal');
  if (!modal) return;

  // 1. Slot must be a direct child of #login-modal, NOT of the form.
  if (slot.parentElement !== modal) {
    modal.appendChild(slot);
  }

  // 2. Slot must be styled as a bottom-center toast. Apply the
  //    inline CSS in JS so the live server (which doesn't auto-
  //    deploy admin/index.php) gets the same behaviour as the
  //    canonical markup. Safe to set repeatedly — last write wins
  //    and the values don't change.
  slot.style.position = 'fixed';
  slot.style.bottom = '5px';
  slot.style.left = '50%';
  slot.style.transform = 'translateX(-50%)';
  slot.style.zIndex = '60';
  // pointer-events: none on the slot so it doesn't block clicks
  // when empty. Each chip opts back in via pointer-events: auto
  // (set in prependLoginError when the chip is created) so the
  // click-to-dismiss gesture still works.
  slot.style.pointerEvents = 'none';
}

// POST /api/logout with keepalive:true so the request completes even if
// the page reloads immediately after. Wipe in-page shell before the reload
// so the user doesn't see a flash of admin chrome.
export async function doLogout(
  state: TimestopState,
  config: ApiFetchConfig,
  apiFetchFn: ApiFetchFn,
  hooks: { clearAppShell: () => void },
): Promise<void> {
  // Web Push (Phase 14 / docs/pwa-plan.md §3): chain the
  // push unsubscribe on logout so the server row is removed
  // and the browser-side PushManager subscription is torn
  // down. Best-effort — a failure here doesn't block logout
  // (the server row will be cleaned up by the dispatcher's
  // 404/410 path on the next delivery attempt). Raced
  // against the logout POST so a stuck network doesn't delay
  // the page reload.
  void logoutUnsubscribePush(state, config);
  await apiFetchFn('logout', { method: 'POST', keepalive: true }, config);
  // Wipe in-memory auth state so the next page load doesn't accidentally
  // see stale user info.
  state.currentUser = null;
  state.csrfToken = null;
  hooks.clearAppShell();
  window.location.reload();
}